// app/components/AboutUsSectionClient.tsx
'use client';

import React from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import Image from 'next/image';
import { PortableTextBody } from '@/components/PortableTextBody';
import Underline from '@/components/ui/underline';

export type AboutCard = {
    _key: string;
    value: string;
    cardNumber: string | null;
    title: string | null;
    text: string | null;
};

type Props = {
    tagline: string;
    heading: string;
    body: PortableTextBlock[];
    imgSrc: string | null;
    imgBlur: string | null;
    cards: AboutCard[];
};

const SIZES = '(max-width: 768px) 100vw, (max-width: 1280px) 33vw, 460px';

const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

export default function AboutUsSectionClient({
                                                 tagline,
                                                 heading,
                                                 body,
                                                 imgSrc,
                                                 imgBlur,
                                                 cards,
                                             }: Props) {
    const alt = heading || 'About image';
    const [taglineFirstWord, ...taglineRest] = tagline.split(' ');

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.05 }}
                className="px-[5%] pt-16 pb-10 md:pt-24 md:pb-12 lg:pt-28 lg:pb-14 overflow-visible"
            >
                <div className="container mx-auto">

                    {/* =========================
              JEDEN H1 – nad wszystkimi breakpointami (SEO)
              Tagline + H1 w jednym miejscu, layout responsive
          ========================== */}
                    <div className="mx-auto max-w-7xl">
                        <div className="grid items-start gap-6 md:grid-cols-12 md:gap-8 lg:gap-12">
                            <div className="md:col-span-2">
                                <div className="relative inline-block">
                                    <m.p custom={0} variants={slideUp} className="uppercase tracking-[0.10em] text-sm">
                                        <span className="lg:hidden">{tagline}</span>
                                        <span className="hidden lg:inline">
                                            {taglineFirstWord}
                                            {taglineRest.length > 0 && (
                                                <>
                                                    <br />
                                                    {taglineRest.join(' ')}
                                                </>
                                            )}
                                        </span>
                                    </m.p>
                                    <Underline
                                        className="pointer-events-none absolute -left-2 top-full -mt-1 text-accent h-5 w-[150px] md:h-6 md:w-[180px]"
                                        delay={0.8}
                                        useWhileInView
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-8 lg:pl-8 xl:pl-12">
                                <m.h1 custom={1} variants={slideUp} className="font-medium leading-tight text-5xl md:text-9xl">
                                    {heading}
                                </m.h1>
                            </div>
                        </div>
                    </div>

                    {/* =========================
              MOBILE (BEZ ZMIAN)
              < md
          ========================== */}
                    <div className="block md:hidden">
                        <div className="mx-auto max-w-7xl">
                            <div className="mt-6 md:mt-12 grid">
                                <div>
                                    <div className="grid gap-6">
                                        <m.div custom={2} variants={slideUp}>
                                            <PortableTextBody value={body} paragraphClassName="text-base" />
                                        </m.div>
                                        <m.div custom={3} variants={slideUp} className="shrink-0">
                                            <span className="block h-10 w-28" aria-hidden="true" />
                                        </m.div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 grid items-start gap-8">
                            <div>
                                {imgSrc && (
                                    <div className="relative mx-auto aspect-[3/4] w-full max-w-[420px]">
                                        <div className="relative w-full h-full overflow-hidden rounded-3xl bg-primary-light shadow-sm ios-clip-fix">
                                            <div className="absolute inset-0">
                                                <Image
                                                    src={imgSrc}
                                                    alt={alt}
                                                    fill
                                                    sizes={SIZES}
                                                    className="object-cover"
                                                    placeholder={imgBlur ? 'blur' : 'empty'}
                                                    blurDataURL={imgBlur ?? undefined}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <ul className="relative grid gap-5">
                                    {cards.map((card, i) => (
                                        <m.li
                                            key={card._key}
                                            custom={4 + i}
                                            variants={slideUp}
                                            className="rounded-3xl bg-primary-light p-4 shadow-md"
                                        >
                                            <div className="mb-3 flex items-start justify-between">
                                                <div className="text-4xl font-medium leading-none tracking-tighter">{card.value}</div>
                                                {card.cardNumber && <div className="text-xs font-mono opacity-50">{card.cardNumber}</div>}
                                            </div>
                                            <div className="mb-3 h-px w-full bg-black/20" />
                                            {card.title && <p className="text-md font-medium pt-10">{card.title}</p>}
                                            {card.text && <p className="mt-1 text-base text-neutral-600">{card.text}</p>}
                                        </m.li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* =========================
              TABLET (NOWY UKŁAD)
              md .. < lg
          ========================== */}
                    <div className="hidden md:block lg:hidden">
                        <div className="mx-auto max-w-7xl">
                            <div className="grid gap-6">
                                {imgSrc && (
                                    <m.div custom={2} variants={slideUp} className="mt-6">
                                        <div className="relative mx-auto aspect-[3/4] w-full max-w-[460px]">
                                            <div className="relative w-full h-full overflow-hidden rounded-3xl bg-primary-light shadow-sm ios-clip-fix">
                                                <Image
                                                    src={imgSrc}
                                                    alt={alt}
                                                    fill
                                                    sizes={SIZES}
                                                    className="object-cover"
                                                    placeholder={imgBlur ? 'blur' : 'empty'}
                                                    blurDataURL={imgBlur ?? undefined}
                                                />
                                            </div>
                                        </div>
                                    </m.div>
                                )}

                                <div className="mt-4 grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
                                    <m.div custom={3} variants={slideUp} className="md:max-w-[37rem]">
                                        <PortableTextBody value={body} paragraphClassName="text-md" />
                                    </m.div>
                                    <m.div custom={4} variants={slideUp} className="shrink-0 md:pt-1 justify-self-end">
                                        <span className="block h-10 w-28" aria-hidden="true" />
                                    </m.div>
                                </div>

                                <ul className="relative grid gap-5 md:grid-cols-3 md:items-stretch md:pt-24">
                                    {cards.map((card, i) => (
                                        <m.li
                                            key={card._key}
                                            custom={5 + i}
                                            variants={slideUp}
                                            className="rounded-3xl bg-primary-light p-5 shadow-md"
                                        >
                                            <div className="mb-3 flex items-start justify-between">
                                                <div className="text-4xl lg:text-[3.2rem] font-medium leading-none tracking-tighter">
                                                    {card.value}
                                                </div>
                                                {card.cardNumber && <div className="text-xs font-mono opacity-50">{card.cardNumber}</div>}
                                            </div>
                                            <div className="mb-3 h-px w-full bg-black/20" />
                                            {card.title && <p className="text-lg font-medium pt-20">{card.title}</p>}
                                            {card.text && <p className="mt-1 text-base text-neutral-600">{card.text}</p>}
                                        </m.li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* =========================
              DESKTOP
              >= lg
          ========================== */}
                    <div className="hidden lg:block">
                        <div className="mx-auto max-w-7xl">
                            <div className="mt-12 md:mt-24 grid md:grid-cols-12">
                                <div className="md:col-start-6 md:col-span-7">
                                    <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
                                        <m.div custom={2} variants={slideUp} className="md:max-w-[37rem]">
                                            <PortableTextBody
                                                value={body}
                                                paragraphClassName="text-base md:text-md"
                                            />
                                        </m.div>
                                        <m.div custom={3} variants={slideUp} className="shrink-0 md:pt-1 justify-self-end">
                                            <span className="block h-10 w-28" aria-hidden="true" />
                                        </m.div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 grid items-start gap-8 md:grid-cols-12">
                            <div className="md:col-span-4">
                                {imgSrc && (
                                    <div className="relative mx-auto aspect-[3/4] w-full max-w-[420px] lg:max-w-[460px] md:bottom-16">
                                        <div className="relative w-full h-full overflow-hidden rounded-3xl bg-primary-light shadow-sm ios-clip-fix">
                                            <div className="hidden mouse:block absolute inset-0 overflow-hidden">
                                                <m.div
                                                    initial={{ y: '-100%' }}
                                                    animate={{ y: 0 }}
                                                    transition={{ duration: 1.2, ease: [0.45, 0, 0.55, 1] }}
                                                    className="absolute inset-0 overflow-hidden"
                                                >
                                                    <m.div
                                                        initial={{ y: '100%', scale: 1.1 }}
                                                        animate={{ y: 0, scale: 1 }}
                                                        transition={{ duration: 1.2, ease: [0.45, 0, 0.55, 1] }}
                                                        className="relative w-full h-full"
                                                    >
                                                        <Image
                                                            src={imgSrc}
                                                            alt={alt}
                                                            fill
                                                            sizes={SIZES}
                                                            className="object-cover"
                                                            placeholder={imgBlur ? 'blur' : 'empty'}
                                                            blurDataURL={imgBlur ?? undefined}
                                                            unoptimized
                                                        />
                                                    </m.div>
                                                </m.div>
                                            </div>

                                            <div className="absolute inset-0 mouse:hidden">
                                                <Image
                                                    src={imgSrc}
                                                    alt={alt}
                                                    fill
                                                    sizes={SIZES}
                                                    className="object-cover"
                                                    placeholder={imgBlur ? 'blur' : 'empty'}
                                                    blurDataURL={imgBlur ?? undefined}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="md:col-span-8">
                                <ul className="relative grid gap-5 md:grid-cols-3 md:items-end md:pt-12">
                                    {cards.map((card, i) => (
                                        <m.li
                                            key={card._key}
                                            custom={4 + i}
                                            variants={slideUp}
                                            className={`${
                                                i === 0 ? 'md:mb-14' : i === 1 ? 'md:mb-[-60px]' : 'md:mb-2'
                                            } rounded-3xl bg-primary-light p-4 md:p-5 shadow-md`}
                                        >
                                            <div className="mb-3 flex items-start justify-between">
                                                <div className="text-4xl lg:text-[3.2rem] font-medium leading-none tracking-tighter">
                                                    {card.value}
                                                </div>
                                                {card.cardNumber && <div className="text-xs font-mono opacity-50">{card.cardNumber}</div>}
                                            </div>
                                            <div className="mb-3 h-px w-full bg-black/20" />
                                            {card.title && <p className="text-md md:text-lg font-medium pt-10 md:pt-20">{card.title}</p>}
                                            {card.text && <p className="mt-1 text-base text-neutral-600">{card.text}</p>}
                                        </m.li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                </div>
            </m.section>
        </LazyMotion>
    );
}
